import cloneDeep from "lodash/cloneDeep";

import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import { fetchEnumsFromSchema } from "@/core/tools/forms/fetch_enums_from_schema";
import { generateFormObject } from "@/core/tools/forms/generate_form_object";
import { prepareFormObjectForSave } from "@/core/tools/forms/prepare_form_object_for_save";

interface CounterpartyFormState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  document: any;
  product_rels: any[];
  targetForDelete: any[];
  form: any;
  schema: any;
  savedBusinessScopes: any[];
  parentProductName: string | null;

  meta: any;
}

const state: CounterpartyFormState = {
  loading: false,
  loaded: false,
  fatalError: false,

  document: null,
  product_rels: [],
  targetForDelete: [],
  form: null,
  schema: null,
  savedBusinessScopes: [],
  parentProductName: null,

  meta: {}
};

const initState: CounterpartyFormState = cloneDeep(state);

export const PRODUCT_DATA = "GET_PRODUCT_DATA";
export const PRODUCT_RELS_DATA = "GET_PRODUCT_RELS_DATA";
export const PRODUCT_SCHEMA = "GET_PRODUCT_SCHEMA";
export const GET_PRODUCT_BUSINESS_SCOPES = "GET_PRODUCT_BUSINESS_SCOPES";

export const actions: ActionTree<CounterpartyFormState, RootState> = {
  reset({ commit }) {
    commit("RESET_STATE");
  },

  init({ commit, dispatch }, params) {
    return dispatch("loadData", params);
  },

  loadData({ commit, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q(PRODUCT_DATA)
          .raw("documents.get")
          .action({
            id: params.id
          }),
        vm
          .$q(GET_PRODUCT_BUSINESS_SCOPES)
          .raw("documents.list")
          .action({
            columns: {
              id: "product_business_scopes.id",
              product_id: "product_business_scopes.product_id",
              business_scope_id: "product_business_scopes.business_scope_id",
              name: "business_scopes.name"
            },
            from: "s:product_business_scopes",
            from_as: "product_business_scopes",
            joins: [
              {
                join: "s:business_scopes",
                as: "business_scopes",
                on: {
                  $eq: [
                    "business_scopes.id",
                    "product_business_scopes.business_scope_id"
                  ]
                }
              }
            ],
            where: {
              $eq: ["product_business_scopes.product_id", { $: params.id }]
            },
            page_size: 100000
          })
      ])
        .then((result: ServerResult) => {
          const productData = result.getOperation(PRODUCT_DATA).getContent();
          const businessScopes =
            result.getOperation(GET_PRODUCT_BUSINESS_SCOPES).getContent()
              .documents || [];

          commit("SET_FORM_DATA", {
            ...productData,
            businessScopes
          });

          if (productData.document && productData.document.type_id) {
            dispatch("loadParentProductName", productData.document.type_id);
          } else {
            commit("SET_PARENT_PRODUCT_NAME", null);
          }

          commit("LOADED_SUCCESS");
          resolve(null);
        })
        .catch((error: any) => {
          console.log("error", error);
          commit("LOADED_ERROR");
          reject();
        });
    });
  },

  loadParentProductName({ commit }, typeId) {
    const vm = (this as any)._vm;

    return vm
      .$db([
        vm
          .$q("LOAD_PRODUCT_PARENT_NAME")
          .raw("documents.get")
          .action({
            schema: "products_types",
            id: typeId,
            columns: {
              name: "products_types.name"
            }
          })
      ])
      .then((result: ServerResult) => {
        const content = result
          .getOperation("LOAD_PRODUCT_PARENT_NAME")
          .getContent();
        const name =
          content.document && content.document.name
            ? content.document.name
            : null;
        commit("SET_PARENT_PRODUCT_NAME", name);
        return name;
      })
      .catch((error: any) => {
        console.error("[ProductForm.loadParentProductName] error", error);
        commit("SET_PARENT_PRODUCT_NAME", null);
        return null;
      });
  },

  prepareNew({ commit, state }, initParams) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q(PRODUCT_SCHEMA)
          .raw("schemas.get")
          .action({
            id: `s:products`
          })
      ])
        .then((result: ServerResult) => {
          commit("GENERATE_FORM_DATA", {
            ...result.getOperation(PRODUCT_SCHEMA).getContent(),
            initParams
          });

          commit("LOADED_SUCCESS");
          resolve(null);
        })
        .catch((error: any) => {
          console.log("error", error);
          commit("LOADED_ERROR");
          reject();
        });
    });
  },

  saveForm({ commit, state }, documentId) {
    const vm = (this as any)._vm;

    const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
    // business_scope_ids — это many-to-many связь, сохраняется отдельно
    delete normalizedForm.business_scope_ids;

    const selectedBusinessScopeIds = state.form.business_scope_ids || [];

    console.debug("[ProductForm.save]", {
      type_id: normalizedForm.type_id
    });

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      let saveQuery = null;

      if (documentId) {
        saveQuery = vm
          .$q("PRODUCT_DATA_SAVE")
          .update(documentId, normalizedForm)
          .createManyRelation(
            "product_business_scopes",
            "product_id",
            (createRelated: any) => {
              const oldScopeIds = state.savedBusinessScopes.map(
                (scope: any) => scope.business_scope_id
              );
              return selectedBusinessScopeIds
                .filter((scopeId: any) => !oldScopeIds.includes(scopeId))
                .map((scopeId: any) =>
                  createRelated({ business_scope_id: scopeId })
                );
            }
          )
          .deleteManyRelation(
            "product_business_scopes",
            "product_id",
            (deleteRelated: any) => {
              return state.savedBusinessScopes
                .filter(
                  (scope: any) =>
                    !selectedBusinessScopeIds.includes(scope.business_scope_id)
                )
                .map((scope: any) => deleteRelated(scope));
            }
          );
      } else {
        saveQuery = vm
          .$q("PRODUCT_DATA_SAVE")
          .create("s:products", normalizedForm)
          .withManyRelation(
            "product_business_scopes",
            "product_id",
            (createRelated: any) => {
              return selectedBusinessScopeIds.map((scopeId: any) =>
                createRelated({ business_scope_id: scopeId })
              );
            }
          );
      }

      vm.$db([saveQuery])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("PRODUCT_DATA_SAVE");

          if (operationResult.hasError()) {
            console.log("error", operationResult.getContent());

            commit("LOADED_SUCCESS");
            reject();
          } else {
            const savedContent = operationResult.getContent();
            commit("ON_FORM_SAVE", savedContent);

            // Обновляем savedBusinessScopes на основе текущих значений формы
            commit(
              "UPDATE_SAVED_BUSINESS_SCOPES",
              state.form.business_scope_ids || []
            );

            commit("LOADED_SUCCESS");
            resolve(savedContent);
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  },

  changeFormValue({ commit, dispatch }, { field, newValue }) {
    commit("CHANGE_VALUE", { field, newValue });

    if (field === "type_id") {
      if (newValue) {
        dispatch("loadParentProductName", newValue);
      } else {
        commit("SET_PARENT_PRODUCT_NAME", null);
      }
    }
  },

  addAttachments({ state, dispatch }, { newValue }) {
    state.form.attachments = [
      newValue,
      ...(state.form.attachments ? state.form.attachments : [])
    ];
  }
};

export const mutations: MutationTree<CounterpartyFormState> = {
  RESET_STATE: state => {
    for (const key in initState) {
      (state as any)[key] = (initState as any)[key];
    }
  },

  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },

  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },

  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_FORM_DATA(state, { document, schema, businessScopes }) {
    state.document = document;
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);

    state.form = generateFormObject(schema, state.document);

    // Сохраняем загруженные связи для последующего сравнения при сохранении
    state.savedBusinessScopes = businessScopes || [];

    // Преобразуем связи в массив ID для поля business_scope_ids
    if (businessScopes && Array.isArray(businessScopes)) {
      state.form.business_scope_ids = businessScopes.map(
        (scope: any) => scope.business_scope_id
      );
    } else {
      state.form.business_scope_ids = [];
    }
  },

  GENERATE_FORM_DATA(state, { schema, initParams }) {
    state.document = {};
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);

    state.form = generateFormObject(schema, initParams);
  },

  ON_FORM_SAVE(state, { document, schema }) {
    state.document = document;
    state.schema = schema;

    // Сохраняем текущие значения business_scope_ids перед генерацией формы
    const currentScopeIds = state.form.business_scope_ids || [];

    state.form = generateFormObject(schema, state.document);

    // Восстанавливаем business_scope_ids, так как generateFormObject не включает many-to-many связи
    state.form.business_scope_ids = currentScopeIds;
  },

  UPDATE_SAVED_BUSINESS_SCOPES(state, scopeIds) {
    // Обновляем savedBusinessScopes на основе сохраненных ID
    // Создаем объекты связей для последующего сравнения
    state.savedBusinessScopes = scopeIds.map((scopeId: any) => ({
      business_scope_id: scopeId
    }));
  },

  SET_PARENT_PRODUCT_NAME(state, name) {
    state.parentProductName = name;
  },

  CHANGE_VALUE(state: CounterpartyFormState, { field, newValue }) {
    switch (field) {
      default:
        state.form[field] = newValue;
    }
  }
};

const getters = {
  fields: (state: CounterpartyFormState) => {
    return state.schema.fields.reduce((prev: any, field: any) => {
      prev[field.code] = field;
      return prev;
    }, {});
  }
};

export const STORE_KEY = "$_product_catalog_product_form";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
